import { PropositionDetails } from "@dune/types";
import {
	Badge,
	Button,
	Card,
	DocumentPreviewIcon,
	ExternalLinkIcon,
} from "@dune/ui";
import {
	formatCurrency,
	getPropositionOfferTypeBadgeColor,
	getPropositionOfferTypeLabel,
} from "@dune/utils";
import { Eye } from "@untitled-ui/icons-react";
import { Download, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { useDocumentSelection } from "../../../../hooks/panel-sync/useDocumentSelection";
import { useDocumentStore } from "../../../../stores/document.store";
import { useCurrentPanel } from "../../../../stores/panel.store";
import { ReservesBlock } from "../ReservesBlock";
import { LiftReservesModal } from "../modals/LiftReservesModal";
import { downloadFileWithName } from "../../../../utils/document.utils";

interface GeneralTabProps {
	proposition: PropositionDetails;
	isSyndic?: boolean;
}

export const GeneralTab: React.FC<GeneralTabProps> = ({
	proposition,
	isSyndic = false,
}) => {
	const [isReservesModalOpen, setIsReservesModalOpen] = useState(false);
	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);
	const selectDocument = useDocumentStore((state) => state.selectDocument);
	const currentPanel = useCurrentPanel();
	const isPanelOpen = !!currentPanel;

	useDocumentSelection({
		entityType: "PROPOSITION",
		entityId: proposition.id,
	});

	const site = proposition.constructionSite ?? proposition.gfaConstructionSite;
	if (!site) {
		return null;
	}
	const isGFA = !!proposition.gfaConstructionSite;

	const initialPricingDetails = site.constructionSiteOffers
		.find(
			(offer) => offer.binderWarranty.warrantyType === proposition.productType,
		)
		?.pricingDetails.find((pricing) => pricing.type === "INITIAL");

	const totalClientPrimeTTCFromInitialPricing =
		initialPricingDetails?.totalClientPrimeTTC ?? 0;
	const brokerRemunerationFromInitialPricing =
		initialPricingDetails?.brokerRemuneration ?? 0;

	return (
		<div className="space-y-6">
			{proposition.reservesActive && (
				<div className="py-4">
					<ReservesBlock
						reserves={proposition.reservesDescription}
						isActive={proposition.reservesActive}
						onLiftReserves={() => setIsReservesModalOpen(true)}
					/>
				</div>
			)}

			<div
				className={`grid gap-6 ${isPanelOpen ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}
			>
				<div className="bg-gray-100 p-6 rounded-xl">
					<div className="flex items-center mb-4">
						<Badge
							variant="outline"
							className={`${getPropositionOfferTypeBadgeColor(proposition.productType)} uppercase font-semibold text-xs`}
						>
							{getPropositionOfferTypeLabel(proposition.productType)}
						</Badge>
					</div>
					<Card className="p-6 bg-white border shadow-none">
						<div className="space-y-4">
							<div className="flex justify-between items-center">
								<span className="text-sm font-medium text-gray-700">
									Assureur
								</span>
								<span className="text-sm font-semibold text-gray-900">
									{proposition.assureurName}
								</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-sm font-medium text-gray-700">
									Prime TTC
								</span>
								<span className="text-sm font-semibold text-gray-900">
									{formatCurrency(totalClientPrimeTTCFromInitialPricing)}
								</span>
							</div>
							{!isSyndic && (
								<div className="flex justify-between items-center">
									<span className="text-sm font-medium text-gray-700">
										Rémunération Apporteur
									</span>
									<span className="text-sm font-semibold text-gray-900">
										{formatCurrency(brokerRemunerationFromInitialPricing)}
									</span>
								</div>
							)}
						</div>
					</Card>
				</div>

				<div className="bg-gray-100 p-6 rounded-lg">
					<div className="flex items-center mb-4">
						<h3 className="text-gray-700 font-semibold uppercase text-sm">
							Proposition
						</h3>
					</div>
					<Card className="p-4 bg-white border shadow-none">
						<div className="space-y-4 h-full">
							{proposition.documents && proposition.documents.length > 0 ? (
								proposition.documents.map((document) => (
									<div
										key={document.id}
										className="flex items-center justify-between"
									>
										<div className="flex items-center space-x-3 min-w-0 flex-1">
											<DocumentPreviewIcon className="flex-shrink-0" />
											<div className="min-w-0 flex-1">
												<div className="font-medium text-sm text-gray-900 truncate">
													{document.file.name}
												</div>
												<div className="text-xs text-gray-500">
													{document.createdAt
														? new Date(document.createdAt).toLocaleDateString(
																"fr-FR",
															)
														: "jj/mm/aaaa"}
												</div>
											</div>
										</div>
										<div className="flex items-center space-x-2 flex-shrink-0">
											<Button
												variant="ghost"
												size="sm"
												className="text-gray-600 hover:text-gray-900"
												disabled={loadingDocumentId === document.id.toString()}
												onClick={async () => {
													if (document.file?.key) {
														const docId = document.id.toString();
														setLoadingDocumentId(docId);
														try {
															if (
																!document.file.key.includes("/") &&
																/^\d+$/.test(document.file.key)
															) {
																throw new Error(
																	"Le fichier semble être corrompu. Veuillez contacter le support.",
																);
															}

															const url = await getDocumentUrl({
																bucket: "private",
																key: document.file.key,
															});

															if (url) {
																selectDocument(
																	url,
																	document.file.id,
																	document.file.mimeType,
																	document.file.name,
																);
															}
														} catch (error) {
															alert(
																error instanceof Error
																	? error.message
																	: "Erreur lors de l'accès au document",
															);
														} finally {
															setLoadingDocumentId(null);
														}
													} else {
														alert(
															"Impossible d'accéder au document : clé de fichier manquante",
														);
													}
												}}
											>
												{loadingDocumentId === document.id.toString() ? (
													<Loader2 className="w-4 h-4 animate-spin" />
												) : (
													<Eye className="w-4 h-4" />
												)}
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-gray-600 hover:text-gray-900"
												onClick={async () => {
													if (document.file?.key) {
														try {
															if (
																!document.file.key.includes("/") &&
																/^\d+$/.test(document.file.key)
															) {
																throw new Error(
																	"Le fichier semble être corrompu. Veuillez contacter le support.",
																);
															}

															const url = await getDocumentUrl({
																bucket: "private",
																key: document.file.key,
															});

															if (url) {
																await downloadFileWithName(
																	url,
																	document.file.name,
																);
															}
														} catch (error) {
															alert(
																error instanceof Error
																	? error.message
																	: "Erreur lors du téléchargement du document",
															);
														}
													} else {
														alert(
															"Impossible de télécharger le document : clé de fichier manquante",
														);
													}
												}}
											>
												<Download className="w-4 h-4" />
											</Button>
										</div>
									</div>
								))
							) : (
								<div className="text-center py-4 text-gray-500 text-sm">
									Aucun document disponible
								</div>
							)}
						</div>
					</Card>
				</div>
			</div>

			<div
				className={`grid gap-6 ${isPanelOpen ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}
			>
				<Card className="p-6 bg-white border shadow-none">
					<div className="space-y-4">
						<h3 className="text-gray-700 font-semibold uppercase text-sm">
							Chantier
						</h3>
						<Link
							to={isGFA ? `/gfa/${site.id}` : `/projects/${site.id}`}
							className="block"
						>
							<Card className="p-6 bg-white border shadow-none hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<span className="text-sm font-semibold text-gray-900">
											CH-
											{site.id.toString().padStart(4, "0")}
										</span>
										<Badge
											variant="secondary"
											className="bg-green-100 text-green-800 border-green-200"
										>
											En cours
										</Badge>
									</div>
									<ExternalLinkIcon className="size-6 text-dune-primary flex-shrink-0" />
								</div>
							</Card>
						</Link>
					</div>
				</Card>

				<Card className="p-6 bg-white border shadow-none">
					<div
						className={`grid gap-6 ${isPanelOpen || !site.syndic ? "grid-cols-1" : "grid-cols-2"}`}
					>
						<Card className="p-4 border shadow-none">
							<div className="flex items-center gap-2 mb-3">
								<h3 className="text-gray-700 font-semibold uppercase text-sm">
									MOA
								</h3>
								<Link
									to={isGFA ? `/gfa/${site.id}` : `/projects/${site.id}`}
									className="text-dune-primary hover:text-dune-primary-hover flex-shrink-0"
								>
									<ExternalLinkIcon className="size-5" />
								</Link>
							</div>
							{site.constructionSiteClientDetails?.clientIsMOA ? (
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
										<span className="text-gray-600 font-medium text-sm">
											{site.constructionSiteClientDetails?.moaClientType ===
											"USER"
												? `${site.constructionSiteClientDetails?.moaClientContact?.firstname?.[0] || ""}${site.constructionSiteClientDetails?.moaClientContact?.lastname?.[0] || ""}`
												: site.constructionSiteClientDetails?.moaClientSociety
														?.denomination?.[0] || "M"}
										</span>
									</div>
									<div className="min-w-0 flex-1">
										<div className="font-medium text-sm text-gray-900 truncate">
											{site.constructionSiteClientDetails?.moaClientType ===
											"USER"
												? `${site.constructionSiteClientDetails?.moaClientContact?.firstname || ""} ${site.constructionSiteClientDetails?.moaClientContact?.lastname || ""}`
												: site.constructionSiteClientDetails?.moaClientSociety
														?.denomination || "MOA Société"}
										</div>
										{site.constructionSiteClientDetails.moaClientType ===
											"USER" &&
											site.constructionSiteClientDetails.moaClientContact
												?.email && (
												<div className="text-xs text-gray-500 truncate">
													{
														site.constructionSiteClientDetails?.moaClientContact
															.email
													}
												</div>
											)}
									</div>
								</div>
							) : (
								<div className="text-center py-4 text-gray-500 text-sm">
									Pas de MOA identifié
								</div>
							)}
						</Card>

						{site.syndic && (
							<Card className="p-4 border shadow-none">
								<div className="flex items-center gap-2 mb-3">
									<h3 className="text-gray-700 font-semibold uppercase text-sm">
										Syndic
									</h3>
								</div>
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
										<span className="text-white font-medium text-sm">
											{site.syndic.denomination?.[0]}
										</span>
									</div>
									<div className="min-w-0 flex-1">
										<div className="font-medium text-sm text-gray-900 truncate">
											{site.syndic.denomination}
										</div>
										<div className="text-xs text-gray-500 truncate">
											{site.syndic.siren}
										</div>
									</div>
								</div>
							</Card>
						)}
					</div>
				</Card>
			</div>

			<LiftReservesModal
				open={isReservesModalOpen}
				onOpenChange={setIsReservesModalOpen}
				propositionId={proposition.id}
				currentReserves={proposition.reservesDescription}
			/>
		</div>
	);
};
